import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout'));
import LoginPage from '@app/pages/LoginPage';
import SignUpPage from '@app/pages/SignUpPage';
import ForgotPasswordPage from '@app/pages/ForgotPasswordPage';
import NewPasswordPage from '@app/pages/NewPasswordPage';
import LockPage from '@app/pages/LockPage';

import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import ProfileLayout from '@app/components/profile/ProfileLayout';
import RequireAuth from '@app/components/router/RequireAuth';
import RequireAdmin from '@app/components/router/RequireAdmin';
import SmartAuditGate from '@app/components/router/SmartAuditGate';
import { withLoading } from '@app/hocs/withLoading.hoc';

const ServerErrorPage = React.lazy(() => import('@app/pages/ServerErrorPage'));
const Error404Page = React.lazy(() => import('@app/pages/Error404Page'));

const UserManagerPage = React.lazy(() => import('@app/pages/UserManagerPage'));

const PersonalInfoPage = React.lazy(() => import('@app/pages/PersonalInfoPage'));
const SecuritySettingsPage = React.lazy(() => import('@app/pages/SecuritySettingsPage'));
//const PaymentsPage = React.lazy(() => import('@app/pages/PaymentsPage'));

const Logout = React.lazy(() => import('./Logout'));

//fac
const EquipmentManagerFacPage = React.lazy(() => import('@app/pages/fac/EquipmentManagerPage'));
const MasterListFacPage = React.lazy(() => import('@app/pages/fac/MasterListPage'));
const HistoryFacPage = React.lazy(() => import('@app/pages/fac/HistoryPage'));
const IssueQueryFacPage = React.lazy(() => import('@app/pages/fac/IssueQueryPage'));
const AdvancedFormsFacPage = React.lazy(() => import('@app/pages/fac/AdvancedFormsPage'));
const TaskFacPage = React.lazy(() => import('@app/pages/fac/TaskPage'));
const TaskFacFromPage = React.lazy(() => import('@app/pages/fac/TaskFormPage'));
const TaskFacQueryPage = React.lazy(() => import('@app/pages/fac/TaskQueryPage'));
const CheckListFacPage = React.lazy(() => import('@app/pages/fac/CheckListPage'));
const CheckListFacQueryPage = React.lazy(() => import('@app/pages/fac/CheckListQueryPage'));
const CheckListFacFormPage = React.lazy(() => import('@app/pages/fac/CheckListFormPage'));
const ShiftManagementFacPage = React.lazy(() => import('@app/pages/fac/ShiftManagementPage'));
const AllIssuePage = React.lazy(() => import('@app/pages/fac/AllIssueQueryPage'));
const GembaDashboardFacPage = React.lazy(() => import('@app/pages/fac/DashboardPages/GembaDashboardPage'));
const ChartsFacPage = React.lazy(() => import('@app/pages/fac/ChartsPage'));

//wh
const GembaDashboardWhPage = React.lazy(() => import('@app/pages/wh/DashboardPages/GembaDashboardPage'));
const ChartsWhPage = React.lazy(() => import('@app/pages/wh/ChartsPage'));
const IssueQueryWhPage = React.lazy(() => import('@app/pages/wh/IssueQueryPage'));
const HandleIssueWhPage = React.lazy(() => import('@app/pages/wh/HandleIssuePage'));
const AdvancedFormsWhPage = React.lazy(() => import('@app/pages/wh/AdvancedFormsPage'));
const StageManagerWhPage = React.lazy(() => import('@app/pages/wh/StageManagerPage'));
const EquipmentManagerWhPage = React.lazy(() => import('@app/pages/wh/EquipmentManagerPage'));
const ProductManagerWhPage = React.lazy(() => import('@app/pages/wh/ProductManagerPage'));
//cbm
const StageManagerCbmPage = React.lazy(() => import('@app/pages/cbm/StageManagerPage'));
const EquipmentManagerCbmPage = React.lazy(() => import('@app/pages/cbm/EquipmentManagerPage'));
const ProductManagerCbmPage = React.lazy(() => import('@app/pages/cbm/ProductManagerPage'));
const AdvancedFormsCbmPage = React.lazy(() => import('@app/pages/cbm/AdvancedFormsPage'));
const ChartsCbmPage = React.lazy(() => import('@app/pages/cbm/ChartsPage'));
const IssueQueryCbmPage = React.lazy(() => import('@app/pages/cbm/IssueQueryPage'));
const MyTaskCbmPage = React.lazy(() => import('@app/pages/cbm/MyTaskPage'));
const GembaDashboardCbmPage = React.lazy(() => import('@app/pages/cbm/DashboardPages/GembaDashboardPage'));
//int
const GembaDashboardIntPage = React.lazy(() => import('@app/pages/int/DashboardPages/GembaDashboardPage'));
const StageManagerIntPage = React.lazy(() => import('@app/pages/int/StageManagerPage'));
const EquipmentManagerIntPage = React.lazy(() => import('@app/pages/int/EquipmentManagerPage'));
const ProductManagerIntPage = React.lazy(() => import('@app/pages/int/ProductManagerPage'));
const SupplyManagerIntPage = React.lazy(() => import('@app/pages/int/SupplyManagerPage'));
const ErrorCategoryManagerIntPage = React.lazy(() => import('@app/pages/int/ErrorCategoryManagerPage'));
const ErrorCodeManagerIntPage = React.lazy(() => import('@app/pages/int/ErrorCodeManagerPage'));
const IssueQueryIntPage = React.lazy(() => import('@app/pages/int/IssueQueryPage'));
const ChartsIntPage = React.lazy(() => import('@app/pages/int/ChartsPage'));
const AdvancedFormsIntPage = React.lazy(() => import('@app/pages/int/AdvancedFormsPage'));
//iqc
const GembaDashboardIqcPage = React.lazy(() => import('@app/pages/iqc/DashboardPages/GembaDashboardPage'));
const ChartsIqcPage = React.lazy(() => import('@app/pages/iqc/ChartsPage'));
const StageManagerIqcPage = React.lazy(() => import('@app/pages/iqc/StageManagerPage'));
const EquipmentManagerIqcPage = React.lazy(() => import('@app/pages/iqc/EquipmentManagerPage'));
const ProductManagerIqcPage = React.lazy(() => import('@app/pages/iqc/DashboardPages/ProductManagerPage'));
const AdvancedFormsIqcPage = React.lazy(() => import('@app/pages/iqc/AdvancedFormsPage'));
const IssueQueryIqcPage = React.lazy(() => import('@app/pages/iqc/IssueQueryPage'));
const HandleIssueIqcPage = React.lazy(() => import('@app/pages/iqc/HandleIssuePage'));
//calib (tool-management)
const ToolManagementDashboardPage = React.lazy(
  () => import('@app/components/tool-management/ToolManagementDashboard/ToolManagementDashboard'),
);
const CalibDashboardPage = React.lazy(
  () => import('@app/components/tool-management/ToolManagementDashboardContent/ToolManagementDashboardContent'),
);
const CalibQuickScanPage = React.lazy(
  () => import('@app/components/tool-management/pages/QuickScanPage/QuickScanPage'),
);
const CalibDeviceInfoListPage = React.lazy(
  () => import('@app/components/tool-management/pages/DeviceInfoPage/DeviceInfoListPage'),
);
const CalibTaskInfoListPage = React.lazy(
  () => import('@app/components/tool-management/pages/TaskInfoPage/TaskInfoListPage'),
);
const CalibBulkConfirmPage = React.lazy(
  () => import('@app/components/tool-management/pages/TaskInfoPage/BulkConfirmPage'),
);
const CalibUpdateCertificatePage = React.lazy(
  () => import('@app/components/tool-management/pages/UpdateCertificatePage/UpdateCertificatePage'),
);
const CalibReplacementRecommendationPage = React.lazy(
  () => import('@app/components/tool-management/pages/ReplacementRecommendationPage/ReplacementRecommendationPage'),
);
const CalibDeviceInfoPage = React.lazy(
  () => import('@app/components/tool-management/pages/DeviceInfoPage/DeviceInfoPage'),
);
const CalibActionsPage = React.lazy(() => import('@app/components/tool-management/pages/ActionsPage/ActionsPage'));
const CalibAccountManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/UserManagementPage/UserManagementPage'),
);
const CalibLocationManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/LocationManagementPage/LocationManagementPage'),
);
const CalibCategoryManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/CategoryManagementPage/CategoryManagementPage'),
);
const CalibCalibrationUnitManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/CalibrationUnitManagementPage/CalibrationUnitManagementPage'),
);
const CalibRepairUnitManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/RepairUnitManagementPage/RepairUnitManagementPage'),
);

//spa
const StageManagerPageSpaPage = React.lazy(() => import('@app/pages/spa/StageManagerPage'));
const IssueOwnerPageSpaPage = React.lazy(() => import('@app/pages/spa/IssueOwnerPage'));
const EquipmentManagerSpaPage = React.lazy(() => import('@app/pages/spa/EquipmentManagerPage'));
const ProductManagerSpaPage = React.lazy(() => import('@app/pages/spa/ProductManagerPage'));
const QualityCodeManagerSpaPage = React.lazy(() => import('@app/pages/spa/QualityCodeManagerPage'));
const GembaDashboardSpaPage = React.lazy(() => import('@app/pages/spa/DashboardPages/GembaDashboardPage'));
const IssueQuerySpaPage = React.lazy(() => import('@app/pages/spa/IssueQueryPage'));
const ChartsSpaPage = React.lazy(() => import('@app/pages/spa/ChartsPage'));
const AdvancedFormsSpaPage = React.lazy(() => import('@app/pages/spa/AdvancedFormsPage'));

export const NFT_DASHBOARD_PATH = '/';
export const MEDICAL_DASHBOARD_PATH = '/gemba-dashboard';

const UserManager = withLoading(UserManagerPage);
// UI Components

const ServerError = withLoading(ServerErrorPage);
const Error404 = withLoading(Error404Page);

// Profile
const PersonalInfo = withLoading(PersonalInfoPage);
const SecuritySettings = withLoading(SecuritySettingsPage);
//const Payments = withLoading(PaymentsPage);

const AuthLayoutFallback = withLoading(AuthLayout);
const LogoutFallback = withLoading(Logout);

//fac

const MasterListFac = withLoading(MasterListFacPage);
const CheckListFac = withLoading(CheckListFacPage);
const CheckListFacQuery = withLoading(CheckListFacQueryPage);
const CheckListFacForm = withLoading(CheckListFacFormPage);
const GembaDashboardFac = withLoading(GembaDashboardFacPage);
const AdvancedFormFac = withLoading(AdvancedFormsFacPage);
const IssueQueryFac = withLoading(IssueQueryFacPage);
const TaskFac = withLoading(TaskFacPage);
const TaskFacForm = withLoading(TaskFacFromPage);
const TaskFacQuery = withLoading(TaskFacQueryPage);
const AllIssueFilterFac = withLoading(AllIssuePage);
const ChartsFac = withLoading(ChartsFacPage);
const ShiftManagementFac = withLoading(ShiftManagementFacPage);
const EquipmentManagerFac = withLoading(EquipmentManagerFacPage);
const HistoryFac = withLoading(HistoryFacPage);

//fac
const GembaDashboardWh = withLoading(GembaDashboardWhPage);
const ChartsWh = withLoading(ChartsWhPage);
const AdvancedFormWh = withLoading(AdvancedFormsWhPage);
const IssueQueryWh = withLoading(IssueQueryWhPage);
const HandleIssueWh = withLoading(HandleIssueWhPage);
const StageManagerWh = withLoading(StageManagerWhPage);
const EquipmentManagerWh = withLoading(EquipmentManagerWhPage);
const ProductManagerWh = withLoading(ProductManagerWhPage);

//cbm
const GembaDashboardCbm = withLoading(GembaDashboardCbmPage);
const IssueQueryCbm = withLoading(IssueQueryCbmPage);
const MyTaskCbm = withLoading(MyTaskCbmPage);
const AdvancedFormCbm = withLoading(AdvancedFormsCbmPage);
const StageManagerCbm = withLoading(StageManagerCbmPage);
const EquipmentManagerCbm = withLoading(EquipmentManagerCbmPage);
const ProductManagerCbm = withLoading(ProductManagerCbmPage);
const ChartsCbm = withLoading(ChartsCbmPage);
//int
const StageManagerInt = withLoading(StageManagerIntPage);
const EquipmentManagerInt = withLoading(EquipmentManagerIntPage);
const ProductManagerInt = withLoading(ProductManagerIntPage);
const SupplyManagerInt = withLoading(SupplyManagerIntPage);
const ErrorCategoryManagerInt = withLoading(ErrorCategoryManagerIntPage);
const ErrorCodeManagerInt = withLoading(ErrorCodeManagerIntPage);
const GembaDashboardInt = withLoading(GembaDashboardIntPage);
const IssueQueryInt = withLoading(IssueQueryIntPage);
const AdvancedFormInt = withLoading(AdvancedFormsIntPage);
const ChartsInt = withLoading(ChartsIntPage);
//iqc
const GembaDashboardIqc = withLoading(GembaDashboardIqcPage);
const StageManagerIqc = withLoading(StageManagerIqcPage);
const EquipmentManagerIqc = withLoading(EquipmentManagerIqcPage);
const ProductManagerIqc = withLoading(ProductManagerIqcPage);
const ChartsIqc = withLoading(ChartsIqcPage);
const AdvancedFormIqc = withLoading(AdvancedFormsIqcPage);
const IssueQueryIqc = withLoading(IssueQueryIqcPage);
const HandleIssueIqc = withLoading(HandleIssueIqcPage);
//spa
const IssueOwnerSpa = withLoading(IssueOwnerPageSpaPage);
const StageManagerSpa = withLoading(StageManagerPageSpaPage);
const EquipmentManagerSpa = withLoading(EquipmentManagerSpaPage);
const ProductManagerSpa = withLoading(ProductManagerSpaPage);
const QualitycodeManagerSpa = withLoading(QualityCodeManagerSpaPage);
const ChartsSpa = withLoading(ChartsSpaPage);
const GembaDashboardSpa = withLoading(GembaDashboardSpaPage);
const AdvancedFormSpa = withLoading(AdvancedFormsSpaPage);
const IssueQuerySpa = withLoading(IssueQuerySpaPage);

//calib
const ToolManagementDashboard = withLoading(ToolManagementDashboardPage);
const CalibDashboard = withLoading(CalibDashboardPage);
const CalibQuickScan = withLoading(CalibQuickScanPage);
const CalibDeviceInfoList = withLoading(CalibDeviceInfoListPage);
const CalibTaskInfoList = withLoading(CalibTaskInfoListPage);
const CalibBulkConfirm = withLoading(CalibBulkConfirmPage);
const CalibUpdateCertificate = withLoading(CalibUpdateCertificatePage);
const CalibReplacementRecommendation = withLoading(CalibReplacementRecommendationPage);
const CalibDeviceInfo = withLoading(CalibDeviceInfoPage);
const CalibActions = withLoading(CalibActionsPage);
const CalibAccountManagement = withLoading(CalibAccountManagementPage);
const CalibLocationManagement = withLoading(CalibLocationManagementPage);
const CalibCategoryManagement = withLoading(CalibCategoryManagementPage);
const CalibCalibrationUnitManagement = withLoading(CalibCalibrationUnitManagementPage);
const CalibRepairUnitManagement = withLoading(CalibRepairUnitManagementPage);

//kaizen
const KaizenToolManagementDashboardPage = React.lazy(
  () => import('@app/components/tool-management/ToolManagementDashboard/ToolManagementDashboard'),
);
const KaizenToolManagementDashboardContent = React.lazy(
  () => import('@app/components/tool-management/ToolManagementDashboardContent/ToolManagementDashboardContent'),
);
const KaizenActionsPage = React.lazy(() => import('@app/components/tool-management/pages/ActionsPage/ActionsPage'));
const KaizenAccountManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/UserManagementPage/UserManagementPage'),
);
const KaizenEmployeeManagementPage = React.lazy(
  () => import('@app/components/tool-management/pages/EmployeeManagementPage/EmployeeManagementPage'),
);

const KaizenToolManagementDashboard = withLoading(KaizenToolManagementDashboardPage);
const KaizenDashboard = withLoading(KaizenToolManagementDashboardContent);
const KaizenActions = withLoading(KaizenActionsPage);
const KaizenAccountManagement = withLoading(KaizenAccountManagementPage);
const KaizenEmployeeManagement = withLoading(KaizenEmployeeManagementPage);

//smart-audit (5S)
const SmartAuditApp = withLoading(React.lazy(() => import('@app/smart-audit')));

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={protectedLayout}>
          <Route path="wh">
            <Route index element={<GembaDashboardWh />} />
            <Route path="issue">
              <Route path="info" element={<AdvancedFormWh />} />
              <Route path="info/:id" element={<AdvancedFormWh />} />
              <Route path="query" element={<IssueQueryWh />} />
              <Route path="handle" element={<HandleIssueWh />} />
            </Route>
            <Route path="charts" element={<ChartsWh />} />
            <Route path="stages" element={<StageManagerWh />}></Route>
            <Route path="equipments" element={<EquipmentManagerWh />}></Route>
            <Route path="products" element={<ProductManagerWh />}></Route>

            <Route path="supply" element={<SupplyManagerInt></SupplyManagerInt>}></Route>
            <Route path="error-category" element={<ErrorCategoryManagerInt></ErrorCategoryManagerInt>}></Route>
            <Route path="error-code" element={<ErrorCodeManagerInt></ErrorCodeManagerInt>}></Route>
            <Route path="users" element={<UserManager></UserManager>} />
            <Route path="profile" element={<ProfileLayout />}>
              <Route path="personal-info" element={<PersonalInfo />} />
              <Route path="security-settings" element={<SecuritySettings />} />
            </Route>
          </Route>

          <Route path="fac">
            <Route index element={<MasterListFac />}></Route>
            <Route path="check-list">
              <Route path="" element={<CheckListFac />} />
              <Route path="info" element={<CheckListFacForm />} />
              <Route path="info/:id" element={<CheckListFacForm />} />
              <Route path="query" element={<CheckListFacQuery />} />
            </Route>
            <Route path="issue">
              <Route path="" element={<GembaDashboardFac />} />
              <Route path="info" element={<AdvancedFormFac />} />
              <Route path="info/:id" element={<AdvancedFormFac />} />
              <Route path="query" element={<IssueQueryFac />} />
            </Route>
            <Route path="task">
              <Route path="" element={<TaskFac />} />
              <Route path="info" element={<TaskFacForm />} />
              <Route path="info/:id" element={<TaskFacForm />} />
              <Route path="query" element={<TaskFacQuery />} />
            </Route>
            <Route path="all-issue" element={<AllIssueFilterFac />} />
            <Route path="charts" element={<ChartsFac />} />
            <Route path="shiftManagement" element={<ShiftManagementFac />} />
            <Route path="equipments" element={<EquipmentManagerFac></EquipmentManagerFac>}></Route>
            <Route path="history" element={<HistoryFac />} />

            <Route path="users" element={<UserManager></UserManager>} />
            <Route path="profile" element={<ProfileLayout />}>
              <Route path="personal-info" element={<PersonalInfo />} />
              <Route path="security-settings" element={<SecuritySettings />} />
            </Route>
          </Route>

          <Route path="cbm">
            <Route index element={<GembaDashboardCbm />} />
            <Route path="issue">
              <Route path="info" element={<AdvancedFormCbm />} />
              <Route path="info/:id" element={<AdvancedFormCbm />} />
              <Route path="query" element={<IssueQueryCbm />} />
              <Route path="my-tasks" element={<MyTaskCbm />} />
            </Route>
            <Route path="charts" element={<ChartsCbm />} />
            <Route path="stages" element={<StageManagerCbm />}></Route>
            <Route path="equipments" element={<EquipmentManagerCbm></EquipmentManagerCbm>}></Route>
            <Route path="products" element={<ProductManagerCbm></ProductManagerCbm>}></Route>

            <Route path="supply" element={<SupplyManagerInt></SupplyManagerInt>}></Route>
            <Route path="error-category" element={<ErrorCategoryManagerInt></ErrorCategoryManagerInt>}></Route>
            <Route path="error-code" element={<ErrorCodeManagerInt></ErrorCodeManagerInt>}></Route>
            <Route path="users" element={<UserManager></UserManager>} />
            <Route path="profile" element={<ProfileLayout />}>
              <Route path="personal-info" element={<PersonalInfo />} />
              <Route path="security-settings" element={<SecuritySettings />} />
            </Route>
          </Route>

          <Route path="int">
            <Route index element={<GembaDashboardInt />} />
            <Route path="issue">
              <Route path="info" element={<AdvancedFormInt />} />
              <Route path="info/:id" element={<AdvancedFormInt />} />
              <Route path="query" element={<IssueQueryInt />} />
            </Route>
            <Route path="charts" element={<ChartsInt />} />
            <Route path="stages" element={<StageManagerInt></StageManagerInt>}></Route>
            <Route path="equipments" element={<EquipmentManagerInt></EquipmentManagerInt>}></Route>
            <Route path="products" element={<ProductManagerInt></ProductManagerInt>}></Route>
            <Route path="supply" element={<SupplyManagerInt></SupplyManagerInt>}></Route>
            <Route path="error-category" element={<ErrorCategoryManagerInt></ErrorCategoryManagerInt>}></Route>
            <Route path="error-code" element={<ErrorCodeManagerInt></ErrorCodeManagerInt>}></Route>
            <Route path="users" element={<UserManager></UserManager>} />
            <Route path="profile" element={<ProfileLayout />}>
              <Route path="personal-info" element={<PersonalInfo />} />
              <Route path="security-settings" element={<SecuritySettings />} />
            </Route>
          </Route>

          <Route path="iqc">
            <Route index element={<GembaDashboardIqc />} />
            <Route path="issue">
              <Route path="info" element={<AdvancedFormIqc />} />
              <Route path="info/:id" element={<AdvancedFormIqc />} />
              <Route path="query" element={<IssueQueryIqc />} />
              <Route path="handle" element={<HandleIssueIqc />} />
            </Route>
            <Route path="charts" element={<ChartsIqc />} />
            <Route path="stages" element={<StageManagerIqc></StageManagerIqc>}></Route>
            <Route path="equipments" element={<EquipmentManagerIqc></EquipmentManagerIqc>}></Route>
            <Route path="products" element={<ProductManagerIqc></ProductManagerIqc>}></Route>

            <Route path="supply" element={<SupplyManagerInt></SupplyManagerInt>}></Route>
            <Route path="error-category" element={<ErrorCategoryManagerInt></ErrorCategoryManagerInt>}></Route>
            <Route path="error-code" element={<ErrorCodeManagerInt></ErrorCodeManagerInt>}></Route>
            <Route path="users" element={<UserManager></UserManager>} />
            <Route path="profile" element={<ProfileLayout />}>
              <Route path="personal-info" element={<PersonalInfo />} />
              <Route path="security-settings" element={<SecuritySettings />} />
            </Route>
          </Route>

          <Route path="spa">
            <Route index element={<GembaDashboardSpa />} />
            <Route path="issue">
              <Route path="info" element={<AdvancedFormSpa />} />
              <Route path="info/:id" element={<AdvancedFormSpa />} />
              <Route path="query" element={<IssueQuerySpa />} />
            </Route>
            <Route path="charts" element={<ChartsSpa />} />
            <Route path="stages" element={<StageManagerSpa></StageManagerSpa>}></Route>
            <Route path="issueOwner" element={<IssueOwnerSpa></IssueOwnerSpa>}></Route>
            <Route path="equipments" element={<EquipmentManagerSpa></EquipmentManagerSpa>}></Route>
            <Route path="products" element={<ProductManagerSpa></ProductManagerSpa>}></Route>
            <Route path="quality-codes" element={<QualitycodeManagerSpa></QualitycodeManagerSpa>}></Route>
            <Route path="supply" element={<SupplyManagerInt></SupplyManagerInt>}></Route>
            <Route path="error-category" element={<ErrorCategoryManagerInt></ErrorCategoryManagerInt>}></Route>
            <Route path="error-code" element={<ErrorCodeManagerInt></ErrorCodeManagerInt>}></Route>
            <Route path="users" element={<UserManager></UserManager>} />
            <Route path="profile" element={<ProfileLayout />}>
              <Route path="personal-info" element={<PersonalInfo />} />
              <Route path="security-settings" element={<SecuritySettings />} />
            </Route>
          </Route>

          <Route path="calib" element={<ToolManagementDashboard />}>
            <Route index element={<Navigate to="/calib/dashboard" replace />} />
            <Route path="dashboard" element={<CalibDashboard />} />
            <Route path="quick-scan" element={<CalibQuickScan />} />
            <Route path="device-info" element={<CalibDeviceInfoList />} />
            <Route path="task-info/bulk-confirm" element={<CalibBulkConfirm />} />
            <Route path="task-info" element={<CalibTaskInfoList />} />
            <Route path="update-certificate" element={<CalibUpdateCertificate />} />
            <Route path="replacement-recommendation" element={<CalibReplacementRecommendation />} />
            <Route path="device-info/:id" element={<CalibDeviceInfo />} />
            <Route path="actions/create-new" element={<CalibActions />} />
            <Route path="actions/transfer" element={<CalibActions />} />
            <Route path="actions/transfer/:id" element={<CalibActions />} />
            <Route path="actions/calibration" element={<CalibActions />} />
            <Route path="actions/calibration/:id" element={<CalibActions />} />
            <Route path="actions/repair" element={<CalibActions />} />
            <Route path="actions/repair/:id" element={<CalibActions />} />
            <Route path="actions/report-incident" element={<CalibActions />} />
            <Route path="actions/report-incident/:id" element={<CalibActions />} />
            <Route path="actions/stop-using" element={<CalibActions />} />
            <Route path="actions/stop-using/:id" element={<CalibActions />} />
            <Route path="actions/cancel" element={<CalibActions />} />
            <Route path="actions/cancel/:id" element={<CalibActions />} />
            <Route
              path="account-management"
              element={
                <RequireAdmin>
                  <CalibAccountManagement />
                </RequireAdmin>
              }
            />
            <Route path="location-management" element={<CalibLocationManagement />} />
            <Route path="category-management" element={<CalibCategoryManagement />} />
            <Route path="calibration-unit-management" element={<CalibCalibrationUnitManagement />} />
            <Route path="repair-unit-management" element={<CalibRepairUnitManagement />} />
          </Route>

          <Route path="kaizen" element={<KaizenToolManagementDashboard />}>
            <Route index element={<Navigate to="/kaizen/dashboard" replace />} />
            <Route path="dashboard" element={<KaizenDashboard />} />
            <Route path="actions">
              <Route path="create-new" element={<KaizenActions />} />
              <Route path="query" element={<KaizenActions />} />
              <Route path="my-tasks" element={<KaizenActions />} />
              <Route path="task-detail/:id" element={<KaizenActions />} />
              <Route path="task-update/:id" element={<KaizenActions />} />
            </Route>
            <Route
              path="employee-management"
              element={
                <RequireAdmin>
                  <KaizenEmployeeManagement />
                </RequireAdmin>
              }
            />
            <Route
              path="account-management"
              element={
                <RequireAdmin>
                  <KaizenAccountManagement />
                </RequireAdmin>
              }
            />
          </Route>

          <Route path="server-error" element={<ServerError />} />
          <Route path="404" element={<Error404 />} />
        </Route>
        <Route
          path="/smart-audit/*"
          element={
            <RequireAuth>
              <SmartAuditGate>
                <SmartAuditApp />
              </SmartAuditGate>
            </RequireAuth>
          }
        />
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="sign-up" element={<SignUpPage />} />
          <Route
            path="lock"
            element={
              <RequireAuth>
                <LockPage />
              </RequireAuth>
            }
          />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          {/* <Route path="security-code" element={<SecurityCodePage />} /> */}
          <Route path="new-password" element={<NewPasswordPage />} />
        </Route>
        <Route path="/logout" element={<LogoutFallback />} />
      </Routes>
    </BrowserRouter>
  );
};
